import { AuthUtils, LicenseUtils } from '@portal/portal-core';
import { filter as _filter, keyBy } from 'lodash-es';
import { createSelector } from 'reselect';

import * as FILTERS from 'constants/filters';
import { STATUS_ACTIVE, STATUS_INACTIVE } from 'constants/filters';
import { getActiveFilterGroups } from 'constants/filters/filter-config';
import { MASTER_CHECKBOX_ID, TOOL_BUTTON_NAMES } from 'constants/super-admin';
import type { LicenseViewMode } from 'ducks/super-client/actions';
import type { LicenseSearchState } from 'ducks/super-client/reducers/license-search';
import { ccTypes } from 'ducks/super-client/shared';
import { filterSelector, selectLicenseViewModeMap } from 'selectors';
import { baseSelect } from 'selectors/selector-utils';
import { countInvitesForProductAccess } from '@/types/cost-center-invite';
import {
  addSearchKey,
  chainSelectors,
  composeFilters,
  emptyArray,
  emptyObj,
  getSuperAdminLicenseStatus,
  getObjectMatchesFromSearch,
  SEARCH_KEY,
  sortSuperAdmin,
  stripSearchKey,
} from 'utils';
import { formatSearchResults } from 'ducks/super-client/utils';

// The super-admin table rows (licenses, suffixes, etc.) originate from untyped
// `.js` state slices, ducks, and util chains, so both the redux state read here
// and the per-row element data are genuinely `any` at this boundary. A
// row-producing selector is modeled as a function from state to a row array.
type RowSelector = (state: any) => any[];

// ------------------------------------------------------------------------------------------------
// Simple
// ------------------------------------------------------------------------------------------------

export const selectActiveTool = baseSelect('superAdmin.toolsBar.selectedTool', emptyObj);

export const selectActiveFilters = baseSelect('superAdmin.selectedFilters', emptyObj);

export const selectLicenses = baseSelect('superAdmin.superClient.licenses', emptyArray);

export const selectLicenseSearchState = baseSelect('superAdmin.superClient.licenseSearch', {
  hits: emptyArray,
  total: 0,
  limit: 0,
  offset: 0,
  loading: false,
  error: null,
  latestRequestId: null,
}) as (state: unknown) => LicenseSearchState;

// The Create/Manage License modal's live duplicate-cost-center-name check — a dedicated slice,
// separate from licenseSearch above (see ducks/super-client/types' comment on why).
export const selectCostCenterNameCheck = baseSelect('superAdmin.superClient.costCenterNameCheck', {
  name: null,
  relaxedNameMatches: emptyArray,
  matchingLicenseRows: emptyArray,
  pending: false,
  error: null,
});

// Which view (search vs legacy full-sync) each license page is showing. This is the single
// source of truth for the pages and for the global header's keyword search box. A page with no
// entry shows the search view.
const DEFAULT_LICENSE_VIEW_MODE: LicenseViewMode = 'search';

export const selectInstitutionalLicenseViewMode = createSelector(
  [selectLicenseViewModeMap],
  (modes): LicenseViewMode =>
    modes[TOOL_BUTTON_NAMES.INSTITUTION_LICENSE] ?? DEFAULT_LICENSE_VIEW_MODE,
);

export const selectIndividualLicenseViewMode = createSelector(
  [selectLicenseViewModeMap],
  (modes): LicenseViewMode =>
    modes[TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE] ?? DEFAULT_LICENSE_VIEW_MODE,
);

export const selectActiveLicenseIds = baseSelect('superAdmin.selections.licenses', emptyObj);

export const selectActiveUserIds = baseSelect('superAdmin.selections.users', emptyObj);

export const selectLicenseSort = baseSelect('superAdmin.sorts.licenses', emptyObj);

export const selectPendingSuffixes = baseSelect('superAdmin.superClient.suffixes', emptyArray);

export const selectSuffixSort = baseSelect('superAdmin.sorts.suffixes', emptyObj);

export const selectActiveSuffixRows = baseSelect('superAdmin.selections.suffixes', emptyObj);

export const selectActiveCostCenter = baseSelect('superAdmin.selections.costCenter', emptyObj);

export const selectActiveCostCenterInvites = baseSelect('superAdmin.selections.invites', emptyObj);

export const selectAllCostCenterInvites = baseSelect(
  'superAdmin.allCostCenterInvites.invitesByCostCenter',
  emptyObj,
);

export const selectCostCenterInvitesFetchFailed = baseSelect(
  'superAdmin.allCostCenterInvites.fetchFailed',
  false,
);

// Cost centers whose last cached-invites fetch failed, as { [costCenterId]: true }.
export const selectCostCenterInvitesCacheFetchFailed = baseSelect(
  'superAdmin.costCenterInvitesCache.fetchFailedByCostCenterId',
  emptyObj,
);

// Raw cost-center-keyed invite cache (populated by the existing ADMIN_GET_COST_CENTER_INVITES
// action). A consumer that needs a count for one specific license/productAccess - like
// LicenseDetailModal - filters `invitesByCostCenterId[costCenterId]` by `productAccessId` itself;
// each invite already carries its own productAccessId, so no separate scoped fetch/selector is
// needed per license.
export const selectCostCenterInvitesCache = baseSelect(
  'superAdmin.costCenterInvitesCache.invitesByCostCenterId',
  emptyObj,
);

export const selectSuperClientConfirmation = baseSelect(
  'superAdmin.superClient.confirmation',
  emptyObj,
);

export const selectLicenseDetails = baseSelect('superAdmin.selections.licenseDetails', emptyObj);
export const selectLicenseRequest = baseSelect('superAdmin.superClient.licenseRequest', emptyObj);
export const selectUserCCs = baseSelect('superAdmin.superClient.selectedUserCostCenters', emptyObj);

export const selectVerifyEmailsResults = baseSelect(
  'superAdmin.superClient.usersRequest.verifyEmailsResult',
  emptyArray,
);

export const selectUnPhantomUsersResults = baseSelect(
  'superAdmin.superClient.usersRequest.unPhantomUsersResult',
  emptyArray,
);

export const selectUsers = baseSelect('superAdmin.superClient.users', emptyArray);

export const selectUserFieldUpdateError = baseSelect('superAdmin.superClient.userFieldError', null);

export const selectSearchStatus = baseSelect('superAdmin.superClient.searchStatus', {
  requestId: null,
  error: null,
});

export const selectProductFeatures = baseSelect('superAdmin.superClient.productFeatures', emptyObj);

export const selectArchiveActiveEotStatus = baseSelect('superAdmin.superClient.archiveActiveEot', {
  fetchStatus: null,
  fetchError: null,
  eot: null,
  archiveStatus: null,
  archiveError: null,
});

// ------------------------------------------------------------------------------------------------
// Composed
// ------------------------------------------------------------------------------------------------

// Licenses state as object, not array
export const selectLicensesByID = createSelector([selectLicenses], (licenses) =>
  keyBy(licenses, 'productAccess.id'),
);

export const selectFilterIsActive = createSelector([selectActiveFilters], (filters) => {
  // `filters` is an untyped map of filter id -> active flag from the `.js` state slice.
  return Object.entries(filters as Record<string, unknown>).reduce<boolean>(
    (acc, [, isActive]) => acc || Boolean(isActive),
    false,
  );
});

export const selectCombinedFilter = createSelector([selectActiveFilters], (filters) => {
  const isFilterActive = (filterConfig: { id?: string } = {}) =>
    !!(filterConfig.id && (filters as Record<string, unknown>)[filterConfig.id]);
  const filterGroups = getActiveFilterGroups(isFilterActive);
  // `getActiveFilterGroups` types predicates over the concrete `FilterItem` row
  // shape; `composeFilters` types them over `unknown`. The predicates only read
  // row fields, so widening the item type is safe at this boundary.
  return composeFilters(filterGroups as Array<Array<(item: unknown) => boolean>>);
});

// The institutional/individual license tables feed from the same license state and filter it by
// license type. Merge tool state (license type) into overall filters object:
export const selectFiltersWithTool = createSelector(
  [selectActiveTool, selectActiveFilters],
  (selectedTool, filterIds) => {
    // `FILTERS` is a namespace of mixed `FilterOption` constants and factory
    // functions; only the constants carry `tool`/`id`, so read them loosely.
    const toolFilter = (Object.values(FILTERS) as Array<{ id?: string; tool?: string }>).find(
      (filter) => filter.tool === selectedTool,
    );
    if (!toolFilter) {
      return filterIds;
    }
    return {
      [toolFilter.id!]: true,
      ...filterIds,
    };
  },
);

export const deriveLicenseStatusesChainable = (prevSelector: RowSelector) =>
  createSelector([prevSelector], (licenses) =>
    licenses.reduce<any[]>((acc, license) => {
      const { productAccess } = license;
      // License table: status is based on productAccess alone; user-seat checks
      // belong only to the user-detail-modal (user search tool) path.
      productAccess.status = getSuperAdminLicenseStatus(license.costCenter?.type, productAccess);

      return acc.concat([license]);
    }, []),
  );

export const filterLicensesChainable = (prevSelector: RowSelector) =>
  createSelector([prevSelector, selectFiltersWithTool], (licenses, activeFilters) => {
    const isFilterActive = (filterConfig: { id?: string } = {}) =>
      !!(filterConfig.id && (activeFilters as Record<string, unknown>)[filterConfig.id]);
    const filterGroups = getActiveFilterGroups(isFilterActive);
    // See `selectCombinedFilter`: predicate item type widened at this boundary.
    const combinedFilter = composeFilters(filterGroups as Array<Array<(item: unknown) => boolean>>);
    return licenses.filter(combinedFilter);
  });

export const searchLicensesChainable = (prevSelector: RowSelector) =>
  createSelector([prevSelector, filterSelector], (licenses, searchConfig) => {
    const matches = getObjectMatchesFromSearch(
      [SEARCH_KEY],
      // `keyOut` is null here (return whole rows); the `.js` util's JSDoc types it
      // as `string` but treats null as "no keyOut", so cast at this boundary.
      null as unknown as string,
      // `addSearchKey` yields `Record<string, unknown>` rows; the util reads the
      // search-key field (a string) off them, so narrow to its expected shape.
      licenses.map(addSearchKey) as Record<string, string>[],
      searchConfig.filterByValue || '',
    );
    return (matches as Record<string, unknown>[]).map(stripSearchKey);
  });

export const sortLicensesChainable = (prevSelector: RowSelector) =>
  createSelector([prevSelector, selectLicenseSort], (licenses, sortState) =>
    sortSuperAdmin(licenses, sortState),
  );

// Visible licenses after filter/search/sort pipeline.
// `chainSelectors` types its transforms as `(...args: unknown[]) => unknown` and
// returns `unknown`; each chainable takes a `RowSelector`, so widen the transforms
// on the way in and narrow the composed selector back to `RowSelector` on the way out.
export const selectCurrentLicenses = chainSelectors(
  selectLicenses,
  deriveLicenseStatusesChainable as (...args: unknown[]) => unknown,
  filterLicensesChainable as (...args: unknown[]) => unknown,
  searchLicensesChainable as (...args: unknown[]) => unknown,
  sortLicensesChainable as (...args: unknown[]) => unknown,
) as RowSelector;

// Map of cost center ID to invite count
export const selectInviteCountByCostCenterId = createSelector(
  [selectAllCostCenterInvites],
  (invitesByCostCenter) => {
    const inviteCounts: Record<string, number> = {};
    Object.keys(invitesByCostCenter).forEach((costCenterId) => {
      inviteCounts[costCenterId] = invitesByCostCenter[costCenterId]?.length || 0;
    });
    return inviteCounts;
  },
);

// Enhance licenses with precomputed allocated seat counts (active users + pending invites)
export const selectLicensesWithAllocatedSeats = createSelector(
  [selectCurrentLicenses, selectInviteCountByCostCenterId, selectCostCenterInvitesFetchFailed],
  (licenses, inviteCounts, invitesFetchFailed) =>
    // `license` rows originate from untyped `.js` state, so they are `any` here.
    licenses.map((license: any) => {
      const inviteCount = inviteCounts[license.costCenter?.id] || 0;
      const seats = AuthUtils.getSeatUsageForProductAccess(license.productAccess, inviteCount);
      // Use allocatedSeats which includes both users and invites
      const allocatedSeats = seats.allocatedSeats ?? 0;
      const activeSharedSeats = seats.activeSharedSeats ?? null;
      return {
        ...license,
        allocatedSeats,
        activeSharedSeats,
        invitesFetchFailed,
      };
    }),
);

// Visible licenses as object for quick lookups
export const selectCurrentLicensesByID = createSelector([selectCurrentLicenses], (licenses) =>
  keyBy(licenses, 'productAccess.id'),
);

// ------------------------------------------------------------------------------------------------
// License search (server-side, paginated)
// ------------------------------------------------------------------------------------------------

// Flattens raw elasticsearch hits into the same { costCenter, productAccess, admin } row shape
// the legacy license tables consume (see formatSearchResults) and derives the same
// productAccess.status the legacy pipeline computes — allocatedSeats/activeSharedSeats are
// already present on each hit's productAccess entries, computed server-side.
// Depends on `hits` alone so loading, error and request-id changes do not recompute the rows or
// give them a new identity.
const selectLicenseSearchHits = createSelector([selectLicenseSearchState], (state) => state.hits);

const selectLicenseSearchFormattedRows: RowSelector = createSelector(
  [selectLicenseSearchHits],
  (hits) => formatSearchResults(hits),
);

export const selectLicenseSearchRows = chainSelectors(
  selectLicenseSearchFormattedRows,
  deriveLicenseStatusesChainable as (...args: unknown[]) => unknown,
) as RowSelector;

// Search rows as object for quick lookups — mirrors selectLicensesByID/selectCurrentLicensesByID
// above, so row-selection state (selectActiveLicenseRows/selectCurrentLicense below) can
// recognize a selection made against search results, not just the legacy-synced dataset.
export const selectLicenseSearchRowsByID = createSelector([selectLicenseSearchRows], (licenses) =>
  keyBy(licenses, 'productAccess.id'),
);

export const selectLicenseSearchTotal = createSelector(
  [selectLicenseSearchState],
  (state) => state.total,
);

// Cost centers actually returned on the current page — one hit per cost center, before
// flattening into per-productAccess rows (selectLicenseSearchRows). Distinct from `total`
// (every matching cost center across all pages) and from a rows count (one cost center can
// carry multiple productAccess entries/licenses).
export const selectLicenseSearchHitCount = createSelector(
  [selectLicenseSearchState],
  (state) => state.hits.length,
);

export const selectLicenseSearchLimit = createSelector(
  [selectLicenseSearchState],
  (state) => state.limit,
);

export const selectLicenseSearchLoading = createSelector(
  [selectLicenseSearchState],
  (state) => state.loading,
);

export const selectLicenseSearchError = createSelector(
  [selectLicenseSearchState],
  (state) => state.error,
);

// Selected license rows, with search/filtering taken into account: if a row is selected, then
// later filtered away, it will no longer appear in the selector's return value. A selection is
// kept if it's still present in EITHER the legacy view's post-filter set (currentLicensesByID)
// or the search view's current results (searchLicensesByID) — checking both, rather than only
// the legacy one, is what makes a row selected in the search view actually register: previously
// every search-mode selection was immediately filtered away here (currentLicensesByID is only
// ever populated once the legacy full-sync has run), so the checkbox never showed as checked and
// Manage License/Admin View stayed permanently disabled in that view.
export const selectActiveLicenseRows = createSelector(
  [selectActiveLicenseIds, selectCurrentLicensesByID, selectLicenseSearchRowsByID],
  (activeLicenseRows, currentLicensesByID, searchLicensesByID) =>
    Object.keys(activeLicenseRows)
      // If row is filtered away, don't include it:
      .filter(
        (key) =>
          !!currentLicensesByID[key] || !!searchLicensesByID[key] || key === MASTER_CHECKBOX_ID,
      )
      .reduce(
        (total, key) => ({
          ...total,
          [key]: activeLicenseRows[key],
        }),
        {},
      ),
);

// Single selected license object
export const selectCurrentLicense = createSelector(
  [selectLicensesByID, selectLicenseSearchRowsByID, selectActiveLicenseRows],
  (legacyLicenses, searchLicenses, activeRowIDs) => {
    // Same reasoning as selectActiveLicenseRows: the selected id may only exist in the search
    // view's results, not the legacy-synced dataset, so both maps are consulted for the actual
    // license object once selectActiveLicenseRows has already confirmed the id is still valid.
    // legacyLicenses spreads FIRST: it's a one-time snapshot that can go stale (and never
    // clears once the search view is reactivated), while searchLicenses reflects the current
    // query — on a same-id collision the fresher search-derived object should win, not a
    // possibly-stale legacy one.
    const allLicenses = { ...legacyLicenses, ...searchLicenses };
    const activeLicenseRows = Object.keys(activeRowIDs).map((id) => allLicenses[id]);
    return activeLicenseRows[0] || null; // Assumes only one license is checked at a time
  },
);

// This is the same as selectCurrentLicense, but composed from a different base selector.
// In this case, we want to select a license *without* highlighting the table row.
export const selectCurrentLicenseDetails = createSelector(
  [selectLicensesByID, selectLicenseSearchRowsByID, selectLicenseDetails],
  (legacyLicenses, searchLicenses, activeLicenseIDs) => {
    // Same reasoning as selectCurrentLicense: the selected id (productAccess.id, from
    // whichever view the details click came from) may only exist in the search view's results,
    // not the legacy-synced dataset — both maps are consulted so a search-view row's Manage
    // License/Admin View action doesn't resolve to null and crash the details modal's render.
    // legacyLicenses spreads FIRST so a fresher search-derived object wins on a same-id
    // collision instead of a possibly-stale legacy snapshot — see selectCurrentLicense above.
    const allLicenses = { ...legacyLicenses, ...searchLicenses };
    const activeLicense = Object.keys(activeLicenseIDs).map((id) => allLicenses[id])[0];
    return activeLicense || null;
  },
);

// ------------------------------------------------------------------------------------------------

// Pending suffix list as object for quick lookups
export const selectPendingSuffixesByID = createSelector([selectPendingSuffixes], (suffixes) =>
  keyBy(suffixes, 'id'),
);

// @TODO: Refactor filterConfig ('constants/filters/filter-config') to work with suffixes
export const filterSuffixesChainable = (prevSelector: RowSelector) =>
  createSelector([prevSelector, selectActiveFilters], (suffixes, filterData) => {
    const activeFilters: string[] = [];
    Object.entries(filterData).forEach(([filter, isActive]) => {
      if (isActive) {
        activeFilters.push(filter);
      }
    });
    if (activeFilters.length > 0) {
      return suffixes.filter(
        (suffix) => !!activeFilters.find((filter) => filter === suffix.productName),
      );
    }
    return suffixes;
  });

export const sortSuffixesChainable = (prevSelector: RowSelector) =>
  createSelector([prevSelector, selectSuffixSort], (suffixes, sortState) =>
    sortSuperAdmin(suffixes, sortState),
  );

export const searchSuffixesChainable = (prevSelector: RowSelector) =>
  createSelector([prevSelector, filterSelector], (suffixes, searchConfig) => {
    const matches = getObjectMatchesFromSearch(
      [SEARCH_KEY],
      // `keyOut` is null here (return whole rows); the `.js` util's JSDoc types it
      // as `string` but treats null as "no keyOut", so cast at this boundary.
      null as unknown as string,
      // See `searchLicensesChainable`: narrow search-keyed rows to the util shape.
      suffixes.map(addSearchKey) as Record<string, string>[],
      searchConfig.filterByValue || '',
    );
    return (matches as Record<string, unknown>[]).map(stripSearchKey);
  });

// Visible suffixes after sort/search pipeline. See `selectCurrentLicenses` for the
// transform-widening / result-narrowing rationale around `chainSelectors`.
export const selectCurrentSuffixes = chainSelectors(
  selectPendingSuffixes,
  filterSuffixesChainable as (...args: unknown[]) => unknown,
  sortSuffixesChainable as (...args: unknown[]) => unknown,
  searchSuffixesChainable as (...args: unknown[]) => unknown,
) as RowSelector;

export const selectCurrentSuffixesByID = createSelector([selectCurrentSuffixes], (suffixes) =>
  keyBy(suffixes, 'id'),
);

// Selected suffix rows, with search taken into account: if a row is selected, then later filtered
// away, it will no longer appear in the selector's return value.
export const selectActiveSuffixes = createSelector(
  [selectActiveSuffixRows, selectCurrentSuffixesByID],
  (activeRows, currentSuffixesByID) =>
    Object.keys(activeRows)
      // If row is visible (or master), include it:
      .filter((key) => !!currentSuffixesByID[key] || key === MASTER_CHECKBOX_ID)
      .reduce(
        (total, key) => ({
          ...total,
          [key]: activeRows[key],
        }),
        {},
      ),
);

// List of complete objects for each checked suffix
export const selectActiveSuffixesList = createSelector(
  [selectPendingSuffixesByID, selectActiveSuffixes],
  (allSuffixes, selectedSuffixes) =>
    Object.keys(selectedSuffixes)
      .filter((id) => id !== MASTER_CHECKBOX_ID)
      .map((id) => allSuffixes[id]),
);

export const selectLicenseRequestProgress = createSelector(
  [selectLicenseRequest, selectActiveTool],
  (requestdata, activeTool) => {
    const { currentRequest, requestsCount, ccType } = requestdata;
    const safeRequestsCount = Number(requestsCount || 0);
    const safeCurrentRequest = Number(currentRequest || 0);
    if (
      (activeTool === TOOL_BUTTON_NAMES.INSTITUTION_LICENSE && ccType === ccTypes.institutional) ||
      (activeTool === TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE && ccType === ccTypes.individual)
    ) {
      if (safeRequestsCount <= 0) return 100;
      return (safeCurrentRequest / safeRequestsCount) * 100;
    }
    return 100;
  },
);

export const selectIndividualLicenseSyncFailed = createSelector(
  [selectLicenseRequest],
  (requestdata) =>
    Boolean((requestdata as Record<string, unknown>).syncFailed) &&
    (requestdata as Record<string, unknown>).ccType === ccTypes.individual,
);

export const selectInstitutionalLicenseSyncFailed = createSelector(
  [selectLicenseRequest],
  (requestdata) =>
    Boolean((requestdata as Record<string, unknown>).syncFailed) &&
    (requestdata as Record<string, unknown>).ccType === ccTypes.institutional,
);

export const selectIndividualLicenseSyncState = createSelector(
  [selectLicenseRequest],
  (requestdata) => {
    const request = requestdata as Record<string, unknown>;
    if (request.ccType !== ccTypes.individual) {
      return {
        syncFailed: false,
        currentRequest: 0,
        requestsCount: 0,
        nextRequest: 1,
      };
    }

    const currentRequest = Number(request.currentRequest || 0);
    const requestsCount = Number(request.requestsCount || 0);

    return {
      syncFailed: Boolean(request.syncFailed),
      currentRequest,
      requestsCount,
      nextRequest: Number(request.nextRequest || Math.max(1, currentRequest + 1)),
    };
  },
);

export const selectInstitutionalLicenseSyncState = createSelector(
  [selectLicenseRequest],
  (requestdata) => {
    const request = requestdata as Record<string, unknown>;
    if (request.ccType !== ccTypes.institutional) {
      return {
        syncFailed: false,
        currentRequest: 0,
        requestsCount: 0,
        nextRequest: 1,
      };
    }

    const currentRequest = Number(request.currentRequest || 0);
    const requestsCount = Number(request.requestsCount || 0);

    return {
      syncFailed: Boolean(request.syncFailed),
      currentRequest,
      requestsCount,
      nextRequest: Number(request.nextRequest || Math.max(1, currentRequest + 1)),
    };
  },
);

// The saved productAccess of the license currently selected for editing, as held in the active
// cost center; undefined when there is none. Both the seat usage below and the Manage License
// modal's projected seat usage are computed from this one object.
export const selectActiveProductAccess = createSelector(
  [selectActiveLicenseIds, selectActiveCostCenter],
  (activeLicense, costCenter) => {
    const activeLicenseId = Object.keys(activeLicense)[0];
    const { productAccess } = costCenter;
    return _filter(productAccess, (value) => value.id === activeLicenseId)[0];
  },
);

export const selectSeatUsageForProductAccess = createSelector(
  [selectActiveProductAccess, selectActiveCostCenterInvites],
  (currentLicense, invites) => {
    if (!currentLicense) return {};

    // The selected invites are the whole cost center's, across every product; only this
    // license's own count toward its seats.
    const seats = AuthUtils.getSeatUsageForProductAccess(
      currentLicense,
      countInvitesForProductAccess(Object.values(invites), currentLicense.id),
    );
    return seats;
  },
);
